import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, TableHead, TableBody, Grid, TableRow, TableCell, Table, Theme } from '@mui/material';
import { ChevronDown, ChevronUp } from '@assets/svgs';
import { QuoteGetResponse } from '@lio/api-types';
import { TextField } from '@components/textField';
import { useQuoteDiff, useQuoteFormDiff } from '@services/quotes';
import { useSearchPolicyTransactions, useUpdatePolicyTransaction } from '@services/policy';
import { PolicyTransactionTypes } from '@models/policyTransaction';
import Loading from '@components/Loading';
import { Dialog } from '@components/dialog';
import { getTableColumns, formChangeColumns } from '@components/modals/ChangeLogModal/ChangeLogModal.constants';
import { getTypographyVariants } from '@theme/styleOverrides';

type ChangeLogModalProps = {
  isOpen: boolean;
  disabled?: boolean;
  quote?: QuoteGetResponse;
  policyNumber: string;
  setIsOpen: (val: boolean) => void;
  bindPage?: boolean;
  handleBindQuote?: () => void;
  sourceQuoteId?: string;
  hideDescription?: boolean;
  submitButtonLabel?: string;
  isExternalUser?: boolean;
  showFormsSection?: boolean;
  isQuoteRated?: boolean;
};

export const ChangeLogModal: React.FC<ChangeLogModalProps> = ({
  quote,
  disabled,
  isOpen,
  setIsOpen,
  bindPage,
  policyNumber,
  handleBindQuote,
  sourceQuoteId,
  hideDescription,
  submitButtonLabel,
  isExternalUser = false,
  showFormsSection = false,
  isQuoteRated = true,
}) => {
  const effectiveSourceQuoteId = sourceQuoteId ?? quote?.endorsementSourceQuoteId;
  // A staged OoSE quote has no PolicyTransaction of its own; its endorsement transaction (and
  // description) lives on the original quote it replaces. Resolve the transaction against that.
  const transactionQuoteId = quote?.replacesQuoteId ?? quote?.id;

  const { quoteDiff, isPending: isQuoteDiffPending } = useQuoteDiff({
    quoteIdSource: effectiveSourceQuoteId as string,
    quoteIdTarget: quote?.id as string,
    enabled: isOpen,
  });

  const { quoteFormDiff, isPending: isQuoteFormDiffPending } = useQuoteFormDiff({
    quoteIdSource: effectiveSourceQuoteId as string,
    quoteIdTarget: quote?.id as string,
    enabled: isOpen && showFormsSection && isQuoteRated,
  });

  // isLoading (not isPending) so a disabled search — e.g. a staged quote with no policyId — doesn't hang the spinner.
  const { policyTransactions, isLoading: isTransactionsPending } = useSearchPolicyTransactions(
    {
      policyId: quote?.policyId,
      quoteId: transactionQuoteId,
      transactionType: PolicyTransactionTypes.Endorsement,
      pageLimit: 100,
    },
    !hideDescription,
  );

  const { updatePolicyTransaction, isPending } = useUpdatePolicyTransaction();

  const currentTransaction = useMemo(() => {
    if (policyTransactions) {
      const currentEndorsement = policyTransactions.items.filter((el) => el.quote?.id === transactionQuoteId);
      return currentEndorsement[0];
    }
    return undefined;
  }, [policyTransactions, transactionQuoteId]);

  const [description, setDescription] = useState<string | undefined>(currentTransaction?.description ?? undefined);

  const [isDataChangesExpanded, setIsDataChangesExpanded] = useState(true);
  const [isFormChangesExpanded, setIsFormChangesExpanded] = useState(true);

  const columns = useMemo(() => getTableColumns(isExternalUser), [isExternalUser]);

  const tableContent = useMemo(() => {
    if (!quoteDiff?.items) return [];
    return quoteDiff.items.map((item) => {
      return (
        <TableRow key={`${item.quoteItemIdentifier}-${item.propertyName}`} sx={tableRowStyles}>
          {columns.map((col) => {
            return (
              <TableCell sx={[tableCellStyles.body, { padding: '8px 10px' }]} key={col.field}>
                {(item[col.field] as string) ?? '-'}
              </TableCell>
            );
          })}
        </TableRow>
      );
    });
  }, [quoteDiff, columns]);

  const formChangeContent = useMemo(() => {
    if (!showFormsSection || !quoteFormDiff?.items) return [];
    return quoteFormDiff.items.map((item) => {
      const change = item.targetFormExists ? 'Added' : 'Removed';
      const row: Record<string, string> = { formName: item.formName, change };
      return (
        <TableRow key={item.formName} sx={tableRowStyles}>
          {formChangeColumns.map((col) => (
            <TableCell sx={[tableCellStyles.body, { padding: '8px 10px' }]} key={col.field}>
              {row[col.field] ?? '-'}
            </TableCell>
          ))}
        </TableRow>
      );
    });
  }, [quoteFormDiff, showFormsSection]);

  useEffect(() => {
    if (currentTransaction?.description) {
      setDescription(currentTransaction?.description);
    }
  }, [currentTransaction]);

  const handleUpdateSummary = async () => {
    if (currentTransaction) {
      await updatePolicyTransaction(
        {
          policyTransactionId: currentTransaction.id,
          body: {
            status: currentTransaction.status,
            description,
          },
        },
        {
          onError: (e) => console.log(e),
          onSuccess: () => {
            setIsOpen(false);
          },
        },
      );
    }
  };

  const handleBindQuoteUpdateSummary = () => {
    if (!hideDescription) {
      handleUpdateSummary();
    }
    handleBindQuote?.();
  };

  const handleInputChange = (e: string) => {
    setDescription(e);
  };

  const submitButtons = {
    processEndorsement: {
      label: submitButtonLabel ?? 'Process Endorsement',
      onClick: handleBindQuoteUpdateSummary,
      disabled: !hideDescription && (description === '' || description === undefined),
    },
    updateSummary: {
      label: 'Update Summary',
      onClick: handleUpdateSummary,
      disabled: description === currentTransaction?.description,
    },
  };

  const button = bindPage ? submitButtons.processEndorsement : submitButtons.updateSummary;

  const showPrimaryButton = !hideDescription || bindPage;

  const renderEmptyTable = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          height: '75%',
          alignItems: 'center',
        }}
      >
        <Typography variant='bodySmall'>No changes have been made yet</Typography>
      </Box>
    );
  };

  const isFormDiffLoading = showFormsSection && isQuoteRated && isQuoteFormDiffPending;
  const isLoading = hideDescription
    ? isQuoteDiffPending || isFormDiffLoading
    : isTransactionsPending || isQuoteDiffPending || isFormDiffLoading;

  return (
    <Dialog
      handleCloseDialog={() => setIsOpen(false)}
      isPrimaryBtnDisabled={showPrimaryButton ? disabled || button.disabled : undefined}
      isPrimaryBtnLoading={showPrimaryButton ? isPending : undefined}
      open={isOpen}
      primaryBtnText={showPrimaryButton ? button.label : undefined}
      primaryBtnAction={showPrimaryButton ? button.onClick : undefined}
      secondaryBtnAction={() => setIsOpen(false)}
      secondaryBtnText='Exit'
      size='large'
      title={`${bindPage ? 'Final ' : ''}Change Summary for ${policyNumber}`}
      sxProps={{
        paperSx: {
          height: { xs: '100%', sm: '90vh' },
        },
        dialogContent: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          overflowY: 'hidden',
          maxHeight: 'unset',
        },
      }}
    >
      {isLoading ? (
        <Grid
          container
          sx={{
            justifyContent: 'center',
            alignContent: 'center',
            height: '200px',
          }}
        >
          <Loading />
        </Grid>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              padding: '16px 0',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: isDataChangesExpanded ? '1 1 0' : '0 0 auto',
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              {showFormsSection ? (
                <Box
                  role='button'
                  tabIndex={0}
                  aria-expanded={isDataChangesExpanded}
                  aria-label={`${isDataChangesExpanded ? 'Collapse' : 'Expand'} Data Changes section`}
                  onClick={() => setIsDataChangesExpanded((prev) => !prev)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsDataChangesExpanded((prev) => !prev);
                    }
                  }}
                  sx={sectionHeaderStyles}
                >
                  <Typography fontWeight={600}>Data Changes</Typography>
                  {isDataChangesExpanded ? (
                    <ChevronUp width='12px' height='12px' />
                  ) : (
                    <ChevronDown width='12px' height='12px' />
                  )}
                </Box>
              ) : (
                <Typography sx={{ padding: '0 20px 12px 32px', flexShrink: 0 }}>Data Changes</Typography>
              )}
              {isDataChangesExpanded && (
                <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: '0 20px' }}>
                  <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead sx={tableHeadStyles}>
                      <TableRow>
                        {columns.map((col) => (
                          <TableCell
                            sx={[
                              tableCellStyles.header,
                              { padding: '16px 10px', backgroundColor: 'background.paper', width: col.width },
                            ]}
                            key={col.field}
                          >
                            {col.headerName.toUpperCase()}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>{tableContent}</TableBody>
                  </Table>
                  {!tableContent.length ? renderEmptyTable() : null}
                </Box>
              )}
            </Box>
            {showFormsSection && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: isFormChangesExpanded ? '1 1 0' : '0 0 auto',
                  minHeight: 0,
                  overflow: 'hidden',
                }}
              >
                <Box
                  role='button'
                  tabIndex={0}
                  aria-expanded={isFormChangesExpanded}
                  aria-label={`${isFormChangesExpanded ? 'Collapse' : 'Expand'} Form Changes section`}
                  onClick={() => setIsFormChangesExpanded((prev) => !prev)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsFormChangesExpanded((prev) => !prev);
                    }
                  }}
                  sx={sectionHeaderStyles}
                >
                  <Typography fontWeight={600}>Form Changes</Typography>
                  {isFormChangesExpanded ? (
                    <ChevronUp width='12px' height='12px' />
                  ) : (
                    <ChevronDown width='12px' height='12px' />
                  )}
                </Box>
                {isFormChangesExpanded && (
                  <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: '0 20px' }}>
                    {!isQuoteRated ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                        <Typography variant='bodySmall'>Rating is required to analyze form changes</Typography>
                      </Box>
                    ) : (
                      <>
                        <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                          <TableHead sx={tableHeadStyles}>
                            <TableRow>
                              {formChangeColumns.map((col) => (
                                <TableCell
                                  sx={[
                                    tableCellStyles.header,
                                    { padding: '16px 10px', backgroundColor: 'background.paper', width: col.width },
                                  ]}
                                  key={col.field}
                                >
                                  {col.headerName.toUpperCase()}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>{formChangeContent}</TableBody>
                        </Table>
                        {!formChangeContent.length && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                            <Typography variant='bodySmall'>No form changes</Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
          {!hideDescription && (
            <Box sx={{ padding: '10px 20px 20px', flexShrink: 0 }}>
              <TextField
                required={bindPage}
                multiline
                disabled={disabled}
                fullWidth
                rows={8}
                inputLabel='Description'
                value={description}
                onChangeInput={handleInputChange}
                helperText={`${description?.length ?? 0} characters`}
                slotProps={{
                  formHelperText: { sx: { textAlign: 'right' } },
                }}
              />
            </Box>
          )}
        </>
      )}
    </Dialog>
  );
};

const sectionHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  padding: '8px 20px 8px 32px',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: 'action.hover',
  },
};

const tableHeadStyles = ({ palette }: Theme) => ({
  tr: {
    borderBottom: `1px solid ${palette.text.main}30`,
    borderTop: `1px solid ${palette.text.main}30`,
    th: {
      borderBottom: `1px solid ${palette.text.main}30`,
      borderTop: `1px solid ${palette.text.main}30`,
    },
  },
});

const tableCellStyles: any = {
  header: (theme: Theme) => ({
    ...getTypographyVariants(theme.palette.fontSize).tableHeader,
    color: 'text.subdued',
    fontWeight: getTypographyVariants(theme.palette.fontSize).fontWeightBold,
  }),
  body: (theme: Theme) => ({
    ...getTypographyVariants(theme.palette.fontSize).bodySmall,
    wordBreak: 'break-word',
  }),
};

const tableRowStyles = {
  header: {
    borderTop: `2px solid gray`,
    borderColor: 'border.main',
    borderBottom: `2px solid gray`,
    fontWeight: '700',
  },
  body: {
    width: '100%',
  },
  footer: {
    borderTop: `2px solid gray`,
    borderColor: 'border.main',
    borderBottom: 'none',
  },
};
